// Composable for drag-and-drop functionality using Sortable.js
// Provides reusable drag-drop setup for nav items, columns, links
import { onUnmounted } from 'vue'
import Sortable from 'sortablejs'

export function useDragDrop() {
  // Store active sortable instances for cleanup
  const sortableInstances = new Map()

  // Initialize sortable on an element
  function initSortable(element, options = {}) {
    if (!element) {
      console.warn('useDragDrop: Element is null')
      return null
    }

    // Destroy existing instance if any
    destroySortable(element)

    const defaultOptions = {
      animation: 150,
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',
      dragClass: 'sortable-drag',
      handle: '.drag-handle',
      forceFallback: true,
      fallbackClass: 'sortable-fallback',
      fallbackOnBody: true,
      swapThreshold: 0.65
    }

    const sortable = Sortable.create(element, {
      ...defaultOptions,
      ...options,
      onEnd: (evt) => {
        if (options.onReorder) {
          options.onReorder({
            oldIndex: evt.oldIndex,
            newIndex: evt.newIndex,
            item: evt.item
          })
        }
        if (options.onEnd) {
          options.onEnd(evt)
        }
      }
    })

    sortableInstances.set(element, sortable)
    return sortable
  }

  // Destroy sortable instance
  function destroySortable(element) {
    const instance = sortableInstances.get(element)
    if (instance) {
      instance.destroy()
      sortableInstances.delete(element)
    }
  }

  // Destroy all sortable instances
  function destroyAll() {
    sortableInstances.forEach((instance) => {
      instance.destroy()
    })
    sortableInstances.clear()
  }

  // Reorder array helper
  function reorderArray(array, oldIndex, newIndex) {
    const newArray = [...array]
    const [item] = newArray.splice(oldIndex, 1)
    newArray.splice(newIndex, 0, item)
    return newArray
  }

  // Generate unique ID for new items
  function generateId() {
    return crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  // Cleanup on unmount
  onUnmounted(() => {
    destroyAll()
  })

  return {
    initSortable,
    destroySortable,
    destroyAll,
    reorderArray,
    generateId
  }
}

export default useDragDrop
